import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useMediaQuery, createBreakpoints } from 'WeployDesignLanguage/mediaQueries';
import Color from 'color';
import MUIChip from '@material-ui/core/Chip';

import Avatar from 'LegacyWeployDesignLanguage/Avatar/Avatar';
import Checkbox from 'LegacyWeployDesignLanguage/inputs/Checkbox';
import Grid from 'LegacyWeployDesignLanguage/Grid';
import formatCurrency from '../../../utils/Currency';


const StyledWeployeeName = styled.div`
  ${props => props.theme.typography.subtitle1};
`;

const StyledGridContainer = styled(Grid)`
  padding: 0 ${props => props.theme.spacing[2]};
`;

const StyledRow = styled(Grid)`
  ${props => props.theme.typography.body1};
  color: ${props => props.theme.colors.text.primary};
  padding: ${props => props.theme.spacing[2]};
  cursor: pointer;

  &:hover {
    background-color: ${props => Color(props.theme.colors.text.primary).fade(0.88).string()};
  }

  ${props => props.selected && css`
    background-color: ${props.theme.colors.brand.primaryVariant1};
    &:hover {
      background-color: ${props.theme.colors.brand.primary};
    }
  `}
`;

const StyledMobileRow = styled.div`
  ${props => props.theme.typography.body1};
  color: ${props => props.theme.colors.text.primary};
  padding: ${props => props.theme.spacing[2]};
  cursor: pointer;

  ${props => props.selected && css`
    background-color: ${props.theme.colors.brand.primaryVariant1};
  `}

  display: flex;
  justify-content: space-between;
  flex-wrap: nowrap;
  flex-grow: 1;
`;

const StyledDiv = styled(Grid)`
  text-align: right;
`;

const StyledTotalHours = styled.div`
  ${props => props.theme.typography.subtitle1};
`;

const StyledPenaltyHours = styled.div`
  ${props => props.theme.typography.body2};
  color: ${props => props.theme.colors.text.secondary};
`;

const StyledTimestamp = styled.div`
  ${props => props.theme.typography.subtitle2};
  color: ${props => props.theme.colors.text.secondary};
`;

const StyledRejectedChip = styled(MUIChip)`
  background: ${props => props.theme.colors.semantic.warning};
  color: ${props => props.theme.colors.text.primaryOnDark};
`;

const StyledResolvedChip = styled(MUIChip)`
  background: ${props => props.theme.colors.gray};
  color: ${props => props.theme.colors.text.disabled};
`;

const TimesheetRow = ({
  isApproved,
  isRejected,
  isResolved,
  isChecked,
  onClick,
  onCheckChange,
  weployeeAvatarUrl,
  weployeeName,
  jobTitle,
  timesheetPeriod,
  total,
  jobOwner,
  totalHours,
  penaltyHours,
  submittedAgo,
  approvedAgo,
  mobileSelectMode,
  currency,
}) => {
  const { DOWN_SM } = createBreakpoints();
  const mobileUser = useMediaQuery(DOWN_SM);

  const renderAvatar = () => (
    <Avatar
      src={weployeeAvatarUrl}
      alt="Weployee's avatar"
      width="40px"
      height="40px"
    />
  );

  const renderCheckbox = () => (
    <Checkbox
      checked={isChecked}
      onChange={onCheckChange}
      onClick={e => e.stopPropagation()}
      disabled={isRejected}
    />
  );

  const renderMobileRow = () => (
    <StyledMobileRow selected={isChecked} onClick={onClick}>
      {
        mobileSelectMode && !isApproved
        ? (
          renderCheckbox()
        )
        : (
          renderAvatar()
        )
      }
      <Grid item xs={9}>
        <StyledGridContainer container direction="column">
          <Grid item>
            <StyledWeployeeName>{weployeeName}</StyledWeployeeName>
            <div>{jobTitle}</div>
          </Grid>
          <Grid item>{timesheetPeriod}</Grid>
          <Grid item>{formatCurrency(total, currency)}</Grid>
          <Grid item>Owner: {jobOwner}</Grid>
          { isRejected && <Grid item><StyledRejectedChip label="Rejected" /></Grid> }
          { isResolved && <Grid item><StyledResolvedChip label="Resolved" /></Grid> }
        </StyledGridContainer>
      </Grid>
      <StyledDiv item xs={2}>
        <StyledTotalHours>{totalHours}h</StyledTotalHours>
        {
          penaltyHours > 0 &&
          <StyledPenaltyHours>({penaltyHours}h&nbsp;penalty)</StyledPenaltyHours>
        }
        <StyledTimestamp>{isApproved ? approvedAgo : submittedAgo}</StyledTimestamp>
      </StyledDiv>
    </StyledMobileRow>
  );

  if (mobileUser) {
    return renderMobileRow();
  }

  return (
    <StyledRow container justify="space-between" wrap="nowrap" selected={isChecked} onClick={onClick}>
      <Grid item sm={1}>
        <Grid container wrap="nowrap">
          { renderCheckbox() }
          { renderAvatar() }
        </Grid>
      </Grid>
      <Grid item xs={9} lg={10}>
        <StyledGridContainer container direction={mobileUser ? 'column' : 'row'} justify="space-between">
          <Grid item sm={4}>
            <StyledWeployeeName>{weployeeName}</StyledWeployeeName>
            <div>{jobTitle}</div>
          </Grid>
          <Grid item sm={3}>{timesheetPeriod}</Grid>
          <Grid item sm={2}>{formatCurrency(total, currency)}</Grid>
          <Grid item sm={3}>Owner: {jobOwner}</Grid>
        </StyledGridContainer>
        { isRejected && <StyledGridContainer item><StyledRejectedChip label="Rejected" /></StyledGridContainer> }
        { isResolved && <StyledGridContainer item><StyledResolvedChip label="Resolved" /></StyledGridContainer> }
      </Grid>
      <StyledDiv item sm={2} lg={1}>
        <StyledTotalHours>{totalHours}h</StyledTotalHours>
        {
          penaltyHours > 0 &&
          <StyledPenaltyHours>({penaltyHours}h&nbsp;penalty)</StyledPenaltyHours>
        }

        <StyledTimestamp>{isApproved ? approvedAgo : submittedAgo}</StyledTimestamp>
      </StyledDiv>
    </StyledRow>
  );
};

TimesheetRow.propTypes = {
  isApproved: PropTypes.bool,
  isRejected: PropTypes.bool,
  isResolved: PropTypes.bool,
  weployeeAvatarUrl: PropTypes.string.isRequired,
  jobOwner: PropTypes.string.isRequired,
  jobTitle: PropTypes.string.isRequired,
  timesheetPeriod: PropTypes.string.isRequired,
  weployeeName: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  onCheckChange: PropTypes.func,
  isChecked: PropTypes.bool,
  total: PropTypes.number.isRequired,
  totalHours: PropTypes.number.isRequired,
  penaltyHours: PropTypes.number.isRequired,
  submittedAgo: PropTypes.string,
  approvedAgo: PropTypes.string,
  mobileSelectMode: PropTypes.bool,
  currency: PropTypes.string.isRequired,
};

TimesheetRow.defaultProps = {
  isApproved: false,
  isRejected: false,
  isResolved: false,
  isChecked: false,
  onCheckChange: () => {},
  mobileSelectMode: false,
  submittedAgo: '',
  approvedAgo: '',
};

export default TimesheetRow;
